import type { HordeGameIntentDocument } from '@animus/type/domain';
import type { RxConflictHandler, WithDeleted } from 'rxdb/plugins/core';
import { deepEqual } from 'rxdb/plugins/utils';
import { resolveHordeIntentConflict } from './horde-intent-conflict.util';

/** Fresh persistence identity for the ID-only Horde release. */
export const HORDE_RXDB_DATABASE_NAME = 'horde-v4';

export { resolveHordeIntentConflict } from './horde-intent-conflict.util';

/**
 * Resolves conflicts by newest-`updatedAt`-wins instead of RxDB's default master-always-wins (modeled on RxDB's
 * `defaultConflictHandler` shape). Needed because custom spawn brackets replicate peer-to-peer over WebRTC, where
 * either side's local write can be the "master" depending on who pushes first. Equality still deep-compares the
 * whole document - two divergent states sharing an `updatedAt` must be treated as a conflict, not as settled.
 */
export const newestUpdatedAtWinsConflictHandler = <
    DocType extends { updatedAt: string },
>(): RxConflictHandler<DocType> => ({
    isEqual: (a: WithDeleted<DocType>, b: WithDeleted<DocType>): boolean => deepEqual(a, b),
    resolve: (i): Promise<WithDeleted<DocType>> =>
        Promise.resolve(
            i.newDocumentState.updatedAt >= i.realMasterState.updatedAt ? i.newDocumentState : i.realMasterState
        ),
});

/**
 * Host-issued terminal state always wins over a replicated pending copy. This deliberately ignores client clocks:
 * a joiner with a skewed future timestamp cannot resurrect an accepted/rejected intent.
 */
export const hordeIntentConflictHandler: RxConflictHandler<HordeGameIntentDocument> = {
    isEqual: (a: WithDeleted<HordeGameIntentDocument>, b: WithDeleted<HordeGameIntentDocument>): boolean =>
        deepEqual(a, b),
    resolve: ({ newDocumentState, realMasterState }): Promise<WithDeleted<HordeGameIntentDocument>> =>
        Promise.resolve(resolveHordeIntentConflict(newDocumentState, realMasterState)),
};
